import {
  createContext,
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import {
  DEFAULT_LANGUAGE,
  isLanguage,
  translator,
  type Language,
  type Translator,
} from '@praesto/shared'
import { setApiLanguage } from './api.js'

/**
 * Die Sprache der Oberfläche.
 *
 * Drei Quellen, in dieser Reihenfolge: die Einstellung am Konto (sobald jemand
 * angemeldet ist), die zuletzt an diesem Gerät gewählte Sprache, und zuletzt
 * das, was der Browser mitbringt. Das Konto steht vorne, weil es die einzige
 * Quelle ist, die jemand bewusst gesetzt hat und die auf ein fremdes Gerät
 * mitkommt. Der örtliche Merker bleibt trotzdem gepflegt: Er trägt den
 * Anmeldebildschirm, der noch kein Konto kennt, und die Gastansicht, die nie
 * eines haben wird.
 */

const STORAGE_KEY = 'praesto.language'

function stored(): Language | null {
  try {
    const value = window.localStorage.getItem(STORAGE_KEY)
    return isLanguage(value) ? value : null
  } catch {
    // Private-Mode oder gesperrter Speicher — dann eben ohne Merker.
    return null
  }
}

function fromBrowser(): Language | null {
  for (const tag of window.navigator.languages ?? [window.navigator.language]) {
    const base = tag?.toLowerCase().split('-')[0]
    if (isLanguage(base)) return base
  }
  return null
}

function detect(): Language {
  return stored() ?? fromBrowser() ?? DEFAULT_LANGUAGE
}

interface LanguageContextValue {
  language: Language
  t: Translator
  /** Wechselt die Sprache am Gerät. Das Konto pflegt der Aufrufer selbst. */
  setLanguage: (language: Language) => void
}

const LanguageContext = createContext<LanguageContextValue | null>(null)

export function LanguageProvider({ children }: { children: React.ReactNode }): React.ReactElement {
  const [language, setLanguageState] = useState<Language>(detect)

  const setLanguage = useCallback((next: Language) => {
    setLanguageState(next)
    try {
      window.localStorage.setItem(STORAGE_KEY, next)
    } catch {
      // Siehe oben: ohne Speicher gilt die Wahl nur für diese Sitzung.
    }
  }, [])

  // Der Server muss mitbekommen, in welcher Sprache er antworten soll, bevor
  // die erste Anfrage rausgeht — deshalb `useMemo` und nicht `useEffect`.
  useMemo(() => setApiLanguage(language), [language])

  useEffect(() => {
    document.documentElement.lang = language
  }, [language])

  const value = useMemo<LanguageContextValue>(
    () => ({ language, t: translator(language), setLanguage }),
    [language, setLanguage],
  )

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>
}

function useLanguageContext(): LanguageContextValue {
  const value = useContext(LanguageContext)
  if (!value) throw new Error('LanguageProvider fehlt über dieser Komponente.')
  return value
}

/** Der Übersetzer für die aktuelle Sprache — der Normalfall in Komponenten. */
export function useText(): Translator {
  return useLanguageContext().t
}

/**
 * Wie `useText`, aber die Platzhalter dürfen React-Knoten sein.
 *
 * Gedacht für die Handvoll Sätze, in denen ein Wort ausgezeichnet ist — eine
 * fett gesetzte Adresse, ein Link mitten im Text. Die Alternative wäre, solche
 * Sätze in Bruchstücke zu zerlegen; dann könnte kein Übersetzer die Wortstellung
 * mehr ändern, und genau das braucht Englisch an manchen Stellen.
 */
export function useRichText(): (
  key: Parameters<Translator>[0],
  values: Record<string, React.ReactNode>,
) => React.ReactNode {
  const t = useLanguageContext().t
  return useCallback(
    (key, values) =>
      t(key)
        .split(/(\{\w+\})/)
        .map((part, index) => {
          const match = /^\{(\w+)\}$/.exec(part)
          const name = match?.[1]
          if (name && name in values) {
            return <Fragment key={index}>{values[name]}</Fragment>
          }
          return part
        }),
    [t],
  )
}

/** Für die wenigen Stellen, die die Sprache selbst setzen oder anzeigen. */
export function useLanguage(): { language: Language; setLanguage: (language: Language) => void } {
  const { language, setLanguage } = useLanguageContext()
  return { language, setLanguage }
}
